import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './MaterialPreviewModal.module.css';
import '../../../styles/buttons.css';
import type { CloudMaterial } from '../../../features/materials/materialsSlice';
import { useUpdateCloudMaterial, useUpdateTemporaryMaterial, useSaveTemporaryMaterial, useDeleteTemporaryMaterial, useDeleteCloudMaterial, useMoveCloudMaterialToPersonal } from '../../../features/materials/hooks';
import { useToast } from '../../../hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { YouTubeUploadModal, type YouTubeUploadMetadata } from '../../../features/scenarios/components/YouTubeUploadModal';
import { startYouTubeUploadJob } from '../../../features/youtube/uploadJob';
import { startStandaloneSwipeClipJob } from '../../../features/swipeClip/standaloneJob';
import type { SwipeClipParams } from '../../../features/swipeClip/schema';

// スクロール動画のモーダル (Remotion Player を含む) は開いたときだけ読み込む (docs/features/swipe-motion-clip-plan.md §11.4)
const SwipeClipModal = lazy(() => import('../../../features/swipeClip/SwipeClipModal'));
import { DEFAULT_RESOLUTION_PRESETS } from './editing/ImageEditSidebar';
import type { CropState, AspectRatio } from './editing/ImageEditCanvas';
import { ImageEditActionBar } from './editing/ImageEditActionBar';
import { requestTransformPreview, requestTransformSave, requestTransformPreviewTemporary, requestTransformSaveTemporary, fetchMaterialScenarios, replaceMaterialProductLinks, fetchMaterialProductLinks, fetchFacePrivacySanitizedMaterial } from '../../../features/materials/api';
import type { TransformPreviewRequest, ProductLinkInput } from '../../../features/materials/api';
import { fetchMediaPlatforms, fetchMediaMenus, fetchImageSpecs, type MediaPlatform, type MediaMenu, type ImageAssetSpec } from '../../../features/media/api';
import { ProductSelectorModal } from './ProductSelectorModal';
import { brandConfig } from '../../../config/brands';
import { isAxiosError } from 'axios';
import { useMaterialUrls } from '../../../features/materials/useMaterialUrls';
import { useEditInCanva } from '../../../features/canva/hooks';
import { CanvaVideoUploadModal } from '../../../features/canva/CanvaVideoUploadModal';
import { AmazonAdsUploadModal } from './AmazonAdsUploadModal';
import { MaterialPreviewFooter } from './MaterialPreviewFooter';
import { MaterialEditView } from './MaterialEditView';
import { MaterialPreviewView } from './MaterialPreviewView';
import type { MaterialData } from './types';
import { MIN_ZOOM, MAX_ZOOM, ZOOM_STEP } from './types';
import { usePromptModels, type PromptModel } from '../../generation/hooks/usePromptModels';
import { useSeedancePresetActors } from '../../../features/seedance-preset-actors';
import backButtonStyles from '../../common/BackButton.module.css';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { GenerationInfoModal } from './GenerationInfoModal';
import { hasGenerationInfo } from './generationInfoUtils';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useSafeNavigate, useSafeGenerationState } from '../../../hooks/useSafeRouter';

const KNOWN_ASPECT_RATIOS: Record<AspectRatio, number> = {
  custom: 0,
  '1:1': 1,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  '2:3': 2 / 3,
  '3:2': 3 / 2,
  '4:5': 4 / 5,
  '1.91:1': 1.91,
  '1:1.91': 1 / 1.91,
};

function matchKnownAspectRatio(ratioValue: number | null): AspectRatio {
  if (!ratioValue || ratioValue <= 0) {
    return 'custom';
  }
  const tolerance = 0.02;
  for (const [label, numeric] of Object.entries(KNOWN_ASPECT_RATIOS) as Array<[AspectRatio, number]>) {
    if (label === 'custom') continue;
    if (Math.abs(ratioValue - numeric) <= tolerance) {
      return label;
    }
  }
  return 'custom';
}

function inferAspectRatioFromSpec(spec: ImageAssetSpec | null): AspectRatio {
  if (!spec) {
    return 'custom';
  }
  const allowed = spec.allowed_aspect_ratios || [];
  for (const ratio of allowed) {
    if (!ratio) continue;
    if (ratio in KNOWN_ASPECT_RATIOS) {
      return ratio as AspectRatio;
    }
    const parsed = parseFloat(String(ratio).split(':')[0] || '0') / parseFloat(String(ratio).split(':')[1] || '1');
    const matched = matchKnownAspectRatio(Number.isFinite(parsed) ? parsed : null);
    if (matched !== 'custom') {
      return matched;
    }
  }
  const width = spec.width_px ?? spec.min_width_px ?? null;
  const height = spec.height_px ?? spec.min_height_px ?? null;
  if (width && height) {
    return matchKnownAspectRatio(width / height);
  }
  return 'custom';
}

function normalizeAllowedAspectRatios(values: Array<string | number | null | undefined> | undefined): AspectRatio[] {
  if (!values || values.length === 0) {
    return [];
  }

  const result = new Set<AspectRatio>();

  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === 'number') {
      const matched = matchKnownAspectRatio(Number.isFinite(value) ? value : null);
      if (matched !== 'custom') {
        result.add(matched);
      }
      continue;
    }

    const text = String(value).trim();
    if (!text) {
      continue;
    }

    if (text in KNOWN_ASPECT_RATIOS) {
      result.add(text as AspectRatio);
      continue;
    }

    const parts = text.split(':');
    if (parts.length === 2) {
      const left = parseFloat(parts[0] || '0');
      const right = parseFloat(parts[1] || '0');
      if (Number.isFinite(left) && Number.isFinite(right) && right !== 0) {
        const matched = matchKnownAspectRatio(left / right);
        if (matched !== 'custom') {
          result.add(matched);
        }
      }
      continue;
    }

    const numeric = parseFloat(text);
    if (Number.isFinite(numeric)) {
      const matched = matchKnownAspectRatio(numeric);
      if (matched !== 'custom') {
        result.add(matched);
      }
    }
  }

  return Array.from(result);
}

function formatSpecSizeSummary(spec: ImageAssetSpec | null): string | null {
  if (!spec) return null;
  const sizeType = spec.size_constraint_type;
  const width = spec.width_px;
  const height = spec.height_px;
  const minWidth = spec.min_width_px;
  const minHeight = spec.min_height_px;
  const maxWidth = spec.max_width_px;
  const maxHeight = spec.max_height_px;
  const ratios = spec.allowed_aspect_ratios || [];

  const formatPx = (w?: number | null, h?: number | null) => {
    if (!w || !h) return null;
    return `${w}×${h}px`;
  };

  switch (sizeType) {
    case 'fixed':
      return formatPx(width, height);
    case 'min':
      return minWidth && minHeight ? `最低 ${minWidth}×${minHeight}px` : null;
    case 'max':
      return maxWidth && maxHeight ? `最大 ${maxWidth}×${maxHeight}px` : null;
    case 'range':
      return minWidth && minHeight && maxWidth && maxHeight
        ? `範囲 ${minWidth}×${minHeight}px 〜 ${maxWidth}×${maxHeight}px`
        : null;
    case 'min_with_aspect':
      return `${minWidth && minHeight ? `最低 ${minWidth}×${minHeight}px / ` : ''}比率 ${ratios.join(', ') || '指定あり'}`;
    case 'max_with_aspect':
      return `${maxWidth && maxHeight ? `最大 ${maxWidth}×${maxHeight}px / ` : ''}比率 ${ratios.join(', ') || '指定あり'}`;
    case 'aspect_ratio_only':
      return ratios.length ? `比率 ${ratios.join(', ')}` : null;
    default:
      return null;
  }
}

type Props = {
  material: MaterialData;
  materialId?: string;
  onClose: () => void;
  onNavigate?: (direction: 1 | -1) => void;
  /** 削除成功後の遷移先。指定時は onClose の代わりに呼ばれる (親が次の素材へ送る) */
  onDeleted?: (materialId: string) => void;
  showEditControls?: boolean;
  showAddToSelectionButton?: boolean;
  showShareButton?: boolean;
  onRequestRefresh?: (materialId: string) => void;
  isTemporary?: boolean;  // 明示的にtemporary/cloudを指定
};


export function MaterialPreviewModal({ material: initialMaterial, materialId, onClose, onNavigate, onDeleted, showEditControls = false, showAddToSelectionButton = false, showShareButton = true, onRequestRefresh, isTemporary: isTemporaryProp }: Props) {
  const navigate = useSafeNavigate();
  const generationState = useSafeGenerationState();
  const [material, setMaterial] = useState<MaterialData>(initialMaterial);

  // モーダル表示中は背景スクロールを無効化
  useLockBodyScroll(true);

  const [viewMode, setViewMode] = useState<'preview' | 'edit'>('preview');
  const [showGenerationInfo, setShowGenerationInfo] = useState(false);

  // 編集モードのキャンバスサイズ制限に使うビューポートサイズ
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // スマホ判定（CSS の @media (max-width: 768px) と同期）
  const isMobile = useIsMobile();
  const mobileMaxWidth = isMobile ? windowSize.width - 48 : undefined; // 16px padding * 2 + 8px margin * 2
  const mobileMaxHeight = isMobile ? Math.floor(windowSize.height * 0.45) : undefined; // ビューポート高さの45%

  // 画像ローディング状態
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // モバイル: スワイプダウンで閉じるジェスチャー
  const [sheetDragging, setSheetDragging] = useState(false);
  const [sheetDragOffset, setSheetDragOffset] = useState(0);
  const sheetSwipeStartRef = useRef(0);

  const handleSheetSwipeStart = useCallback((e: React.TouchEvent) => {
    sheetSwipeStartRef.current = e.touches[0].clientY;
    setSheetDragging(true);
  }, []);

  const handleSheetSwipeMove = useCallback((e: React.TouchEvent) => {
    if (!sheetDragging) return;
    const deltaY = e.touches[0].clientY - sheetSwipeStartRef.current;
    if (deltaY > 0) {
      setSheetDragOffset(deltaY);
    }
  }, [sheetDragging]);

  const handleSheetSwipeEnd = useCallback(() => {
    setSheetDragging(false);
    if (sheetDragOffset > 100) {
      onClose();
    }
    setSheetDragOffset(0);
  }, [sheetDragOffset, onClose]);

  // プレビューモード時はスマホでピンチズームを有効化
  useEffect(() => {
    if (viewMode === 'preview') {
      const viewport = document.querySelector('meta[name="viewport"]');
      viewport?.setAttribute('content', 'width=device-width, initial-scale=1.0');
      return () => {
        // 元のviewport設定に戻す（index.htmlの設定）
        viewport?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      };
    }
  }, [viewMode]);
  const [notes, setNotes] = useState((material as CloudMaterial).notes ?? '');
  const [materialName, setMaterialName] = useState((material as CloudMaterial).name ?? '');
  const [tags, setTags] = useState([...((material as CloudMaterial).tags ?? [])]);  // Tag[]として推論される
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false); // 編集モード用のダウンロード状態

  // 素材URLを用途別に解決（GCS署名付きURLも自動取得）
  const materialUrls = useMaterialUrls(material);
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [cropState, setCropState] = useState<CropState>(() => ({ x: 0, y: 0, width: 1, height: 1 }));
  const [zoom, setZoom] = useState(1);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [selectedProductLinks, setSelectedProductLinks] = useState<ProductLinkInput[]>([]);
  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
  const [productLinks, setProductLinks] = useState<import('../../../features/materials/api').ProductLink[]>([]);
  const [isProductLinksLoading, setIsProductLinksLoading] = useState(false);
  const [isAdsUploadOpen, setIsAdsUploadOpen] = useState(false);
  const [youtubeUploadOpen, setYoutubeUploadOpen] = useState(false);
  // スクロール動画 (画像素材から・生成キューがある画面だけ)
  const [scrollVideoOpen, setScrollVideoOpen] = useState(false);
  const queryClient = useQueryClient();

  // 個人フォルダの素材かどうかを判定
  // isTemporaryPropが明示的に指定されている場合はそれを使用、そうでなければ
  // folder_type → temp_path の有無の順で判定する。
  // ※ 選択済み素材 (GenerationAsset) 由来の呼び出しは folder_type を持たないため、
  //   ここで cloud に倒すと個人フォルダ素材の商品リンク取得が 404 になる
  const isTemporaryMaterial = isTemporaryProp !== undefined
    ? isTemporaryProp
    : (material.folder_type !== undefined ? material.folder_type === 'temporary' : 'temp_path' in material);
  const folderType = isTemporaryMaterial ? 'temporary' as const : 'cloud' as const;

  // 素材の商品リンクを取得
  useEffect(() => {
    if (!material?.id) return;
    setIsProductLinksLoading(true);
    fetchMaterialProductLinks(folderType, material.id)
      .then(setProductLinks)
      .catch((err) => {
        console.error('商品リンク取得エラー:', err);
        setProductLinks([]);
      })
      .finally(() => setIsProductLinksLoading(false));
  }, [material?.id, folderType]);

  const [mediaPlatforms, setMediaPlatforms] = useState<MediaPlatform[]>([]);
  const [mediaMenus, setMediaMenus] = useState<MediaMenu[]>([]);
  const [mediaSpecs, setMediaSpecs] = useState<ImageAssetSpec[]>([]);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const [selectedSpecId, setSelectedSpecId] = useState<string | null>(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [specLoading, setSpecLoading] = useState(false);
  const [menuGuidelinesState, setMenuGuidelinesState] = useState<string[]>([]);
  const [specGuidelinesState, setSpecGuidelinesState] = useState<string[]>([]);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>('custom');
  const [editState, setEditState] = useState(() => {
    const formatFromMime = material.content_type?.split('/')[1] ?? 'png';
    const normalizedFormat = formatFromMime === 'jpeg' ? 'jpg' : formatFromMime;

    return {
      width: material.width ?? null,
      height: material.height ?? null,
      selectedResolutionId: null as string | null,
      padding: 'none' as 'none' | 'white' | 'transparent',
      format: normalizedFormat,
      quality: 'medium' as 'low' | 'medium-low' | 'medium' | 'medium-high' | 'high',
    };
  });
  const [isTransformApplied, setIsTransformApplied] = useState(false);

  const lastPreviewPayloadRef = useRef<TransformPreviewRequest | null>(null);
  const editSessionInitialRef = useRef<{
    editState: typeof editState;
    selectedAspectRatio: AspectRatio;
    crop: CropState;
    zoom: number;
    previewJobId: string | null;
    previewImageUrl: string | null;
  } | null>(null);

  // 編集モード離脱確認ダイアログ用
  const [showExitEditConfirm, setShowExitEditConfirm] = useState(false);
  const [pendingExitAction, setPendingExitAction] = useState<'preview' | 'close' | null>(null);

  // クロップ関連の変更のみ検知（「切り抜きを適用」「リセット」ボタン用）
  const hasCropChanges = useMemo(() => {
    if (viewMode !== 'edit') return false;
    const initial = editSessionInitialRef.current;
    if (!initial) return false;
    if (previewImageUrl && !initial.previewImageUrl) return true;
    if (selectedAspectRatio !== initial.selectedAspectRatio) return true;
    if (
      cropState.x !== initial.crop.x ||
      cropState.y !== initial.crop.y ||
      cropState.width !== initial.crop.width ||
      cropState.height !== initial.crop.height
    ) return true;
    return false;
  }, [viewMode, previewImageUrl, selectedAspectRatio, cropState]);

  // 全変更検知（離脱確認ダイアログ用）
  const hasEditChanges = useMemo(() => {
    if (hasCropChanges) return true;
    if (viewMode !== 'edit') return false;
    const initial = editSessionInitialRef.current;
    if (!initial) return false;
    if (
      editState.width !== initial.editState.width ||
      editState.height !== initial.editState.height ||
      editState.padding !== initial.editState.padding ||
      editState.format !== initial.editState.format ||
      editState.quality !== initial.editState.quality ||
      editState.selectedResolutionId !== initial.editState.selectedResolutionId
    ) return true;
    return false;
  }, [hasCropChanges, viewMode, editState]);

  const handleExitEditMode = useCallback((action: 'preview' | 'close') => {
    if (hasEditChanges) {
      setPendingExitAction(action);
      setShowExitEditConfirm(true);
    } else if (action === 'preview') {
      setViewMode('preview');
    } else {
      onClose();
    }
  }, [hasEditChanges, onClose]);

  const handleConfirmExitEdit = useCallback(() => {
    setShowExitEditConfirm(false);
    if (pendingExitAction === 'preview') {
      setViewMode('preview');
    } else {
      onClose();
    }
    setPendingExitAction(null);
  }, [pendingExitAction, onClose]);

  const invalidatePreview = useCallback((options?: { dropPreviewImage?: boolean }) => {
    setPreviewJobId(null);
    lastPreviewPayloadRef.current = null;
    setIsTransformApplied(false);
    if (options?.dropPreviewImage === false) {
      return;
    }
    setPreviewImageUrl(null);
  }, []);

  // AI俳優モデル名を取得。model_ids[] は通常AI俳優 (prompt_models) と Seedance preset actor の
  // id が混在しうるため両方を解決し、model_ids[] の順序 (登場人物A/B…) を保ってマージする。
  const modelIds = useMemo(
    () => (Array.isArray(material.model_ids)
      ? material.model_ids.filter((m): m is string => typeof m === 'string' && Boolean(m))
      : []),
    [material.model_ids],
  );
  const { data: promptActorModels } = usePromptModels(
    { ids: modelIds },
    { enabled: modelIds.length > 0 }
  );
  // 生成履歴の表示なので、非アクティブ化された preset も解決できるよう active_only=false。
  const { data: presetActorsResp } = useSeedancePresetActors(
    { active_only: false },
    { enabled: modelIds.length > 0 },
  );
  const actorModels = useMemo<PromptModel[]>(() => {
    if (modelIds.length === 0) return [];
    const promptById = new Map((promptActorModels ?? []).map((m) => [m.id, m]));
    const presetById = new Map((presetActorsResp?.actors ?? []).map((a) => [a.id, a]));
    const out: PromptModel[] = [];
    for (const id of modelIds) {
      const pm = promptById.get(id);
      if (pm) { out.push(pm); continue; }
      const ps = presetById.get(id);
      if (ps) {
        // 名前表示用に PromptModel 形へ最小限正規化 (preset はキャラシート無し)。
        out.push({ id: ps.id, name: ps.display_name, display_name: ps.display_name, face_image_public_url: ps.thumbnail_public_url ?? undefined });
      }
    }
    return out;
  }, [modelIds, promptActorModels, presetActorsResp]);

  // 関連シナリオを取得
  const [isScenariosLoading, setIsScenariosLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;

    if (material.id) {
      setIsScenariosLoading(true);
      fetchMaterialScenarios(material.id)
        .then((scenarios) => {
          if (cancelled) return;
          setMaterial(prev => ({ ...prev, relatedScenarios: scenarios }));
        })
        .catch((error) => {
          console.error('failed to fetch related scenarios', error);
        })
        .finally(() => {
          if (!cancelled) setIsScenariosLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [material.id]);

  useEffect(() => {
    let cancelled = false;
    setPlatformLoading(true);
    fetchMediaPlatforms()
      .then((items) => {
        if (cancelled) return;
        setMediaPlatforms(items);
        setSelectedPlatformId((current) => {
          if (current || items.length !== 1) {
            return current;
          }
          return items[0].id;
        });
      })
      .catch((error) => {
        console.error('failed to fetch media platforms', error);
      })
      .finally(() => {
        if (!cancelled) {
          setPlatformLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectPlatform = useCallback((platformId: string | null) => {
    setSelectedPlatformId(platformId);
    setSelectedMenuId(null);
    setSelectedSpecId(null);
    setMediaMenus([]);
    setMediaSpecs([]);
    setMenuGuidelinesState([]);
    setSpecGuidelinesState([]);
    invalidatePreview();
    setSelectedAspectRatio('custom');
  }, [invalidatePreview]);

  const handleSelectMenu = useCallback(
    (menuId: string | null) => {
      setSelectedMenuId(menuId);
      setSelectedSpecId(null);
      setMediaSpecs([]);
      setSpecGuidelinesState([]);
      invalidatePreview();
      setSelectedAspectRatio('custom');

      if (!menuId) {
        setMenuGuidelinesState([]);
        return;
      }
      const menu = mediaMenus.find((item) => item.id === menuId);
      setMenuGuidelinesState(menu?.guidelines ?? []);
    },
    [invalidatePreview, mediaMenus],
  );

  const handleSelectSpec = useCallback((specId: string | null) => {
    setSelectedSpecId(specId);
    invalidatePreview();
  }, [invalidatePreview]);

  useEffect(() => {
    if (!selectedMenuId) {
      setMediaSpecs([]);
      setSpecGuidelinesState([]);
      return;
    }
    let cancelled = false;
    setSpecLoading(true);
    fetchImageSpecs(selectedMenuId)
      .then((items) => {
        if (cancelled) return;
        setMediaSpecs(items);
        if (items.length === 0) {
          setSelectedSpecId(null);
          setSpecGuidelinesState([]);
          return;
        }
        const defaultSpec = items.find((item) => item.is_default) || items[0];
        setSelectedSpecId((current) => current ?? defaultSpec.id);
        setSpecGuidelinesState(defaultSpec.guidelines || []);
      })
      .catch((error) => {
        console.error('failed to fetch media specs', error);
      })
      .finally(() => {
        if (!cancelled) {
          setSpecLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMenuId]);

  const selectedSpec = useMemo(() => {
    if (!selectedSpecId) return null;
    return mediaSpecs.find((item) => item.id === selectedSpecId) || null;
  }, [selectedSpecId, mediaSpecs]);

const allowedAspectRatiosForSidebar = useMemo(() => {
  if (!selectedSpec) {
    return null;
  }
  const normalized = normalizeAllowedAspectRatios(selectedSpec.allowed_aspect_ratios || []);
  if (normalized.length > 0) {
    return normalized;
  }
  const inferred = inferAspectRatioFromSpec(selectedSpec);
  if (inferred !== 'custom') {
    return [inferred];
  }
  return null;
}, [selectedSpec]);

  useEffect(() => {
    if (!selectedSpec) {
      setSpecGuidelinesState([]);
      setEditState((prev) => {
        if (prev.selectedResolutionId === null) {
          return prev;
        }
        return {
          ...prev,
          selectedResolutionId: null,
        };
      });
      return;
    }
    setSpecGuidelinesState(selectedSpec.guidelines || []);

    const nextWidth = selectedSpec.width_px ?? selectedSpec.min_width_px ?? null;
    const nextHeight = selectedSpec.height_px ?? selectedSpec.min_height_px ?? null;
    const allowedFormats = (selectedSpec.allowed_formats || []).map((fmt) => String(fmt).toLowerCase());

    setEditState((prev) => {
      const updates: Partial<typeof prev> = {};
      if (nextWidth && prev.width !== nextWidth) {
        updates.width = nextWidth;
      }
      if (nextHeight && prev.height !== nextHeight) {
        updates.height = nextHeight;
      }
      if (prev.selectedResolutionId !== selectedSpec.id) {
        updates.selectedResolutionId = selectedSpec.id;
      }
      if (allowedFormats.length > 0) {
        const preferred = allowedFormats.includes((prev.format || '').toLowerCase())
          ? prev.format
          : allowedFormats[0];
        if (preferred && preferred !== prev.format) {
          updates.format = preferred;
        }
      }
      if (Object.keys(updates).length === 0) {
        return prev;
      }
      autoFitRef.current = true;
      return { ...prev, ...updates };
    });

    const inferredRatio = inferAspectRatioFromSpec(selectedSpec);
    if (inferredRatio !== selectedAspectRatio) {
      setSelectedAspectRatio(inferredRatio);
    }
    invalidatePreview({ dropPreviewImage: false });
  }, [invalidatePreview, selectedAspectRatio, selectedSpec]);

  useEffect(() => {
    if (!allowedAspectRatiosForSidebar || allowedAspectRatiosForSidebar.length === 0) {
      return;
    }
    setSelectedAspectRatio((prev) => (allowedAspectRatiosForSidebar.includes(prev) ? prev : allowedAspectRatiosForSidebar[0]));
  }, [allowedAspectRatiosForSidebar]);

  const handleCropAspectRatioChange = useCallback(
    (ratio: AspectRatio) => {
      if (ratio === selectedAspectRatio) {
        return;
      }
      invalidatePreview({ dropPreviewImage: false });
      setSelectedAspectRatio(ratio);
      setIsTransformApplied(false);
    },
    [invalidatePreview, selectedAspectRatio],
  );

  const platformSelectOptions = useMemo(
    () => mediaPlatforms.map((platform) => ({ id: platform.id, name: platform.display_name })),
    [mediaPlatforms],
  );

  const menuSelectOptions = useMemo(
    () => mediaMenus.map((menu) => ({ id: menu.id, label: menu.display_name, platformId: menu.platform_id, description: menu.description })),
    [mediaMenus],
  );

  const specSelectOptions = useMemo(
    () =>
      mediaSpecs.map((spec) => {
        const allowedRatios = normalizeAllowedAspectRatios(spec.allowed_aspect_ratios || []);
        return {
          id: spec.id,
          label: spec.display_name,
          description: spec.description ?? undefined,
          origin: spec.origin,
          guidelines: spec.guidelines || [],
          menuGuidelines: spec.menu_guidelines || [],
          sizeSummary: formatSpecSizeSummary(spec) ?? undefined,
          allowedAspectRatios: allowedRatios.length > 0 ? allowedRatios : undefined,
        };
      }),
    [mediaSpecs],
  );

  const mediaResolutionPresets = useMemo(() => {
    const baseMap = (Object.keys(DEFAULT_RESOLUTION_PRESETS) as AspectRatio[]).reduce(
      (acc, key) => {
        acc[key] = DEFAULT_RESOLUTION_PRESETS[key].map((item) => ({ ...item }));
        return acc;
      },
      {} as Record<AspectRatio, Array<{ id: string; label: string; width: number; height: number }>>,
    );

    if (!selectedSpec) {
      return baseMap;
    }

    const width = selectedSpec.width_px ?? selectedSpec.min_width_px ?? null;
    const height = selectedSpec.height_px ?? selectedSpec.min_height_px ?? null;
    if (!width || !height) {
      return baseMap;
    }

    const ratioKey = inferAspectRatioFromSpec(selectedSpec);
    const option = {
      id: selectedSpec.id,
      label: `${width}×${height}px${selectedSpec.origin === 'custom' ? '（カスタム）' : ''}`,
      width,
      height,
    };

    if (ratioKey === 'custom') {
      return {
        ...baseMap,
        custom: [option],
      };
    }

    return {
      ...baseMap,
      [ratioKey]: [option],
    };
  }, [selectedSpec]);
  useEffect(() => {
    if (!selectedPlatformId) {
      setMediaMenus([]);
      setMenuGuidelinesState([]);
      return;
    }
    let cancelled = false;
    setMenuLoading(true);
    fetchMediaMenus(selectedPlatformId)
      .then((items) => {
        if (cancelled) return;
        setMediaMenus(items);
        if (items.length === 1) {
          setSelectedMenuId(items[0].id);
          setMenuGuidelinesState(items[0].guidelines ?? []);
        }
      })
      .catch((error) => {
        console.error('failed to fetch media menus', error);
      })
      .finally(() => {
        if (!cancelled) {
          setMenuLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPlatformId]);

  const updateCloudMutation = useUpdateCloudMaterial(material.id);
  const updateTemporaryMutation = useUpdateTemporaryMaterial(material.id);
  const saveTemporaryMutation = useSaveTemporaryMaterial();
  const deleteTemporaryMutation = useDeleteTemporaryMaterial();
  const deleteCloudMutation = useDeleteCloudMaterial();
  const moveToPersonalMutation = useMoveCloudMaterialToPersonal();
  const toast = useToast();
  const { editInCanva, isLoading: isCanvaLoading, videoUploadModal, closeVideoUploadModal } = useEditInCanva();

  // 個人フォルダへ移動の確認モーダル
  const [isMoveToPersonalConfirmOpen, setIsMoveToPersonalConfirmOpen] = useState(false);

  // 使用する更新mutation（個人フォルダと共有フォルダで切り替え）
  const updateMutation = isTemporaryMaterial ? updateTemporaryMutation : updateCloudMutation;

  const canEdit = showEditControls;

  // メモの変更状態を追跡（メモ用のキャンセル・保存ボタン表示用）
  const [originalNotes, setOriginalNotes] = useState((material as CloudMaterial).notes ?? '');
  const isNotesDirty = notes !== originalNotes;

  // 名前の変更状態を追跡（名前用のキャンセル・保存ボタン表示用）
  const [originalMaterialName, setOriginalMaterialName] = useState((material as CloudMaterial).name ?? '');
  const isMaterialNameDirty = materialName !== originalMaterialName;

  // タグ変更時に即座に保存
  const handleTagsChange = async (newTags: typeof tags) => {
    if (!showEditControls) return;
    
    setTags(newTags);
    
    try {
      const tagIds = newTags.map(tag => tag.id);
      await updateMutation.mutateAsync({ tags: tagIds } as any);
      toast.show('タグを更新しました');
      onRequestRefresh?.(material.id);
      // materialの状態も更新
      setMaterial(prev => ({ ...prev, tags: newTags as any }));
    } catch (error) {
      console.error('タグ更新エラー:', error);
      toast.error('タグ更新に失敗しました');
      // エラー時は元に戻す
      setTags([...((material as CloudMaterial).tags ?? [])]);
    }
  };

  // メモ保存
  const handleNotesSave = async () => {
    if (!showEditControls) return;
    
    try {
      await updateMutation.mutateAsync({ notes } as any);
      toast.show('メモを更新しました');
      setOriginalNotes(notes);
      onRequestRefresh?.(material.id);
      // materialの状態も更新
      setMaterial(prev => ({ ...prev, notes } as any));
    } catch (error) {
      console.error('メモ更新エラー:', error);
      toast.error('メモ更新に失敗しました');
    }
  };

  // メモキャンセル
  const handleNotesCancel = () => {
    setNotes(originalNotes);
  };

  // 名前保存
  const handleMaterialNameSave = async () => {
    if (!showEditControls) return;

    const trimmed = materialName.trim();
    try {
      await updateMutation.mutateAsync({ name: trimmed || null } as any);
      toast.show('名前を更新しました');
      setMaterialName(trimmed);
      setOriginalMaterialName(trimmed);
      onRequestRefresh?.(material.id);
      // materialの状態も更新
      setMaterial(prev => ({ ...prev, name: trimmed || null } as any));
    } catch (error) {
      console.error('名前更新エラー:', error);
      toast.error('名前の更新に失敗しました');
    }
  };

  // 名前キャンセル
  const handleMaterialNameCancel = () => {
    setMaterialName(originalMaterialName);
  };

  // 制作進捗 (workflow stage) 変更時に即座に保存 (temporary/cloud 両対応)
  const handleWorkflowStageChange = async (newStageId: string | null) => {
    if (!showEditControls) return;
    try {
      await updateMutation.mutateAsync({ workflow_stage_id: newStageId } as any);
      toast.show('制作進捗を更新しました');
      onRequestRefresh?.(material.id);
      setMaterial((prev) => ({ ...prev, workflow_stage_id: newStageId } as any));
    } catch (error) {
      console.error('制作進捗更新エラー:', error);
      toast.error('制作進捗の更新に失敗しました');
    }
  };

  // 商品選択モーダルを開く
  const openProductSelector = async () => {
    // 既存のリンクを取得（表示用stateも同時に更新）
    try {
      const links = await fetchMaterialProductLinks(folderType, material.id);
      setProductLinks(links);
      setSelectedProductLinks(links.map(l => ({ product_type: l.product_type, product_id: l.product_id })));
    } catch {
      setSelectedProductLinks([]);
    }

    setIsProductSelectorOpen(true);
  };

  // 商品リンクを保存 - 全置換（旧リンク全削除→新リンク追加）
  const handleProductSelectLinks = async (links: ProductLinkInput[]) => {
    if (!showEditControls) return;

    try {
      const updated = await replaceMaterialProductLinks(folderType, material.id, links);
      setProductLinks(updated);
      toast.show(`${brandConfig.labels.productShort}リンクを更新しました`);
      onRequestRefresh?.(material.id);
    } catch (error) {
      console.error('商品リンク更新エラー:', error);
      toast.error(`${brandConfig.labels.productShort}リンク更新に失敗しました`);
    }
  };

  const previewUrl = materialUrls?.fullsize ?? null;
  const videoUrl = materialUrls?.video ?? null;
  // GCS署名付きURLの取得失敗（多くはローカル認証切れ）をプレビューに表示するためのメッセージ
  const mediaLoadError = materialUrls?.signedUrlError ?? null;
  const isVideo = (material.content_type || '').startsWith('video/') ||
                  (previewUrl?.endsWith('.mp4') ?? false) ||
                  (previewUrl?.endsWith('.webm') ?? false);
  const isAudio = (material.content_type || '').startsWith('audio/');
  const audioUrl = isAudio ? previewUrl : null;

  // 動画素材を YouTube へアップロード (ジョブキュー経由・非ブロッキング)。
  // キュー(generationState)が無い画面では起動しない。
  const handleStartMaterialYouTubeUpload = (metadata: YouTubeUploadMetadata) => {
    if (!generationState) {
      toast.error('この画面ではYouTubeアップロードを開始できません');
      return;
    }
    startYouTubeUploadJob(
      {
        activeJobs: generationState.activeJobs,
        setActiveJobs: generationState.setActiveJobs,
        setShowJobQueueModal: generationState.setShowJobQueueModal,
        toast,
        queryClient,
      },
      {
        label: materialName || '素材',
        dedupeKey: material.id,
        materialId: material.id,
        title: metadata.title,
        description: metadata.description,
        privacy: metadata.privacy,
        containsSyntheticMedia: metadata.containsSyntheticMedia,
        prepStageLabel: '動画を準備中',
      },
    );
  };

  /** スクロール動画のジョブを投入してキューで追う。投入エラーはモーダルに出すため投げ返す (§11.4)。 */
  const handleStartScrollVideo = async (params: SwipeClipParams) => {
    if (!generationState) {
      throw new Error('この画面ではスクロール動画を作成できません');
    }
    await startStandaloneSwipeClipJob(
      {
        setActiveJobs: generationState.setActiveJobs,
        setShowJobQueueModal: generationState.setShowJobQueueModal,
        toast,
        queryClient,
      },
      { params, label: materialName || (material as { filename?: string }).filename || '素材' },
    );
  };
  const canStartScrollVideo = !!generationState && !isVideo && !isAudio && (material.content_type ?? '').startsWith('image/');

  // モーダルで表示する画像URL (フルサイズの原本)。ダウンロード・シェア・Canva はこれを使う
  const fullSizeImageUrl = materialUrls?.fullsize ?? null;
  // 画面表示 (詳細プレビュー・編集キャンバス) 用: 非ラスタ原本 (SVG) は固有サイズ (例 170×29) で小さく描かれ、
  // 編集の切り出し枠 (ラスタ版の実寸) とも合わないため、ラスタ版があればそれを表示する。無ければ原本
  const displayImageUrl = materialUrls?.raster ?? fullSizeImageUrl;

  // サニタイズファイル (実在人物フィルタ対策で作成した覆面化/線画の隠しファイル)。
  // 紐付けがあればプレビューに表示トグルを出す。画像素材のみ対象。
  const [facePrivacyTwinUrl, setFacePrivacyTwinUrl] = useState<string | null>(null);
  const [facePrivacyTwinStyle, setFacePrivacyTwinStyle] = useState<'faceless' | 'line_art'>('faceless');
  useEffect(() => {
    setFacePrivacyTwinUrl(null);
    if (!material?.id || isVideo || isAudio) return;
    let cancelled = false;
    fetchFacePrivacySanitizedMaterial(folderType, material.id)
      .then((twin) => {
        if (!cancelled) {
          setFacePrivacyTwinUrl(twin?.material.preview_url || null);
          setFacePrivacyTwinStyle(twin?.style ?? 'faceless');
        }
      })
      .catch(() => {
        // 取得失敗時はトグルを出さないだけ (プレビュー自体は通常どおり)
      });
    return () => { cancelled = true; };
  }, [material?.id, folderType, isVideo, isAudio]);

  const displayPreviewUrl = useMemo(() => {
    if (viewMode === 'edit' && previewImageUrl) {
      return previewImageUrl;
    }
    // 編集モードでもフルサイズ画像 (非ラスタ原本はラスタ版) を使用
    return displayImageUrl;
  }, [displayImageUrl, viewMode, previewImageUrl]);

  const zoomDisplay = useMemo(() => `${Math.round(zoom * 100)}%`, [zoom]);

  const handleZoomStep = useCallback((direction: number) => {
    setZoom((prev) => {
      // 10%刻みにスナップ（100%基準: ...80%, 90%, 100%, 110%, 120%...）
      const snapped = direction > 0
        ? Math.ceil(prev / ZOOM_STEP + 0.001) * ZOOM_STEP
        : Math.floor(prev / ZOOM_STEP - 0.001) * ZOOM_STEP;
      return Math.min(Math.max(snapped, MIN_ZOOM), MAX_ZOOM);
    });
  }, []);

  const autoFitRef = useRef(true);

  const computeCenteredCrop = useCallback(
    (imageWidth: number, imageHeight: number, targetWidth?: number | null, targetHeight?: number | null): CropState => {
      if (!imageWidth || !imageHeight) {
        return { x: 0, y: 0, width: 1, height: 1 };
      }
      const desiredWidth = targetWidth ?? imageWidth;
      const desiredHeight = targetHeight ?? imageHeight;
      if (!desiredWidth || !desiredHeight) {
        return { x: 0, y: 0, width: 1, height: 1 };
      }
      const desiredRatio = desiredWidth / desiredHeight;
      const imageRatio = imageWidth / imageHeight;
      let cropWidth = 1;
      let cropHeight = 1;

      if (imageRatio > desiredRatio) {
        cropHeight = 1;
        cropWidth = desiredRatio * (imageHeight / imageWidth);
      } else {
        cropWidth = 1;
        cropHeight = (imageWidth / imageHeight) / desiredRatio;
      }

      cropWidth = Math.min(Math.max(cropWidth, 0.01), 1);
      cropHeight = Math.min(Math.max(cropHeight, 0.01), 1);
      const x = Math.max((1 - cropWidth) / 2, 0);
      const y = Math.max((1 - cropHeight) / 2, 0);
      return { x, y, width: cropWidth, height: cropHeight };
    },
    []
  );

  const handleImageLoad = useCallback((width: number, height: number) => {
    setNaturalSize({ width, height });
    autoFitRef.current = true;

    // EXIF回転等でブラウザの実際の寸法がeditStateと異なる場合、補正する
    setEditState((prev) => {
      if (prev.width === width && prev.height === height) return prev;
      if (prev.width !== null && prev.height !== null) {
        const isSwapped = prev.width === height && prev.height === width;
        if (isSwapped) {
          // editSessionInitialRefも同時に補正
          if (editSessionInitialRef.current) {
            editSessionInitialRef.current.editState = {
              ...editSessionInitialRef.current.editState,
              width,
              height,
            };
          }
          return { ...prev, width, height };
        }
      }
      return prev;
    });

    // スマホの場合はmaxDisplayWidthでサイズ調整するため、zoomは1に固定
    if (isMobile) {
      setZoom(1);
      // 編集セッション初期状態のズームも更新
      if (editSessionInitialRef.current) {
        editSessionInitialRef.current.zoom = 1;
      }
      return;
    }

    // PC: 初期表示時に画面に収まるズームレベルを計算
    if (viewportRef.current) {
      const viewportRect = viewportRef.current.getBoundingClientRect();
      const pad = 8; // CSS padding: 4px * 2
      const maxWidth = viewportRect.width - pad;
      const maxHeight = viewportRect.height - pad;

      const scaleX = maxWidth / width;
      const scaleY = maxHeight / height;
      const fitScale = Math.min(scaleX, scaleY, 1); // 1を超えないように制限

      setZoom(fitScale);
      // 編集セッション初期状態のズームも更新
      if (editSessionInitialRef.current) {
        editSessionInitialRef.current.zoom = fitScale;
      }
    }
  }, [isMobile]);

  useEffect(() => {
    if (viewMode !== 'edit') {
      return;
    }
    if (!naturalSize) {
      return;
    }
    if (!autoFitRef.current) {
      return;
    }
    const nextCrop = computeCenteredCrop(naturalSize.width, naturalSize.height, editState.width, editState.height);
    autoFitRef.current = false;
    setCropState((prev) => {
      const delta =
        Math.abs(prev.x - nextCrop.x) +
        Math.abs(prev.y - nextCrop.y) +
        Math.abs(prev.width - nextCrop.width) +
        Math.abs(prev.height - nextCrop.height);
      if (delta < 0.0001) {
        return prev;
      }
      return nextCrop;
    });
  }, [computeCenteredCrop, editState.height, editState.width, naturalSize, viewMode]);

  const handleCanvasInteractionStart = useCallback(() => {
    autoFitRef.current = false;
  }, []);

  const buildPreviewPayload = useCallback((): TransformPreviewRequest => {
    return {
      width: editState.width ?? undefined,
      height: editState.height ?? undefined,
      crop: {
        x: Number.isFinite(cropState.x) ? Math.min(Math.max(cropState.x, 0), 1) : 0,
        y: Number.isFinite(cropState.y) ? Math.min(Math.max(cropState.y, 0), 1) : 0,
        width: Number.isFinite(cropState.width) ? Math.min(Math.max(cropState.width, 0), 1) : 1,
        height: Number.isFinite(cropState.height) ? Math.min(Math.max(cropState.height, 0), 1) : 1,
      },
      padding: editState.padding,
      format: editState.format,
      quality: editState.quality,
      preset_id: selectedSpecId ?? undefined,
    };
  }, [
    cropState.height,
    cropState.width,
    cropState.x,
    cropState.y,
    editState.format,
    editState.height,
    editState.padding,
    editState.quality,
    editState.width,
    selectedSpecId,
  ]);

  const handleApplyTransform = useCallback(
    async (options?: { showToast?: boolean; force?: boolean }) => {
      if (!canEdit && !options?.force) {
        if (options?.showToast) {
          toast.show('編集は利用できません');
        }
        return null;
      }

      const payload = buildPreviewPayload();
      const requestPayload: TransformPreviewRequest = {
        ...payload,
        crop: payload.crop ? { ...payload.crop } : undefined,
      };

      setIsPreviewLoading(true);

      try {
        const response = isTemporaryMaterial
          ? await requestTransformPreviewTemporary(material.id, requestPayload)
          : await requestTransformPreview(material.id, requestPayload);

        lastPreviewPayloadRef.current = requestPayload;
        setPreviewJobId(response.job_id ?? null);

        // 新しいプレビュー画像をプリロードしてからstateを更新（画像の瞬間的な消失を防ぐ）
        if (response.preview_url) {
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = response.preview_url!;
          });
        }

        setPreviewImageUrl(response.preview_url ?? null);
        setIsTransformApplied(true);
        if (response.preview_url && options?.showToast) {
          toast.show('プレビューを更新しました');
        }
        return response.preview_url ?? null;
      } catch (error) {
        console.error('preview transform failed', error);
        if (options?.showToast !== false) {
          let message = 'プレビューの更新に失敗しました。';
          if (isAxiosError(error)) {
            const backendMessage = error.response?.data?.error;
            if (typeof backendMessage === 'string' && backendMessage.trim()) {
              message = backendMessage.trim();
            }
          }
          toast.error(message);
        }
        return null;
      } finally {
        setIsPreviewLoading(false);
      }
    },
    [buildPreviewPayload, canEdit, isTemporaryMaterial, material.id, toast],
  );

  const handleCanvasInteractionEnd = useCallback(() => {
    // ドラッグ終了時は特に処理不要（プレビューはユーザーが明示的に適用ボタンで実行）
  }, []);

  useEffect(() => {
    if (viewMode !== 'edit') {
      setIsPreviewLoading(false);
    }
  }, [viewMode]);

  const captureEditSessionState = useCallback(() => {
    const snapshot = {
      editState: { ...editState },
      selectedAspectRatio,
      crop: { ...cropState },
      zoom,
      previewJobId,
      previewImageUrl,
    };
    editSessionInitialRef.current = snapshot;
    setIsTransformApplied(Boolean(snapshot.previewJobId || snapshot.previewImageUrl));
  }, [cropState, editState, previewImageUrl, previewJobId, selectedAspectRatio, zoom]);

  // 編集モードに入ったときのみeditStateをリセット
  const prevViewModeRef = useRef<'preview' | 'edit'>('preview');
  
  useEffect(() => {
    const prevViewMode = prevViewModeRef.current;
    prevViewModeRef.current = viewMode;
    
    if (viewMode === 'edit') {
      // プレビューから編集モードに切り替わったときのみリセット
      if (prevViewMode === 'preview') {
        const formatFromMime = material.content_type?.split('/')[1] ?? 'png';
        const normalizedFormat = formatFromMime === 'jpeg' ? 'jpg' : formatFromMime;
        
        setEditState({
          width: material.width ?? null,
          height: material.height ?? null,
          selectedResolutionId: null,
          padding: 'none',
          format: normalizedFormat,
          quality: 'medium',
        });
      }
      
      if (!editSessionInitialRef.current) {
        captureEditSessionState();
      }
    } else {
      editSessionInitialRef.current = null;
    }
  }, [captureEditSessionState, viewMode, material.content_type, material.width, material.height]);

  const handleReset = useCallback(() => {
    const initial = editSessionInitialRef.current;
    if (initial) {
      setEditState({ ...initial.editState });
      setSelectedAspectRatio(initial.selectedAspectRatio);
      setCropState({ ...initial.crop });
      setZoom(initial.zoom);
      setPreviewJobId(initial.previewJobId);
      setPreviewImageUrl(initial.previewImageUrl);
      setIsTransformApplied(Boolean(initial.previewJobId || initial.previewImageUrl));
    } else {
      const formatFromMime = material.content_type?.split('/')[1] ?? 'png';
      const normalizedFormat = formatFromMime === 'jpeg' ? 'jpg' : formatFromMime;

      setEditState({
        width: material.width ?? null,
        height: material.height ?? null,
        selectedResolutionId: null,
        padding: 'none',
        format: normalizedFormat,
        quality: 'medium',
      });
      setSelectedAspectRatio('custom');
      setCropState({ x: 0, y: 0, width: 1, height: 1 });
      setZoom(1);
      setPreviewJobId(null);
      setPreviewImageUrl(null);
      setIsTransformApplied(false);
    }

    lastPreviewPayloadRef.current = null;
    setIsPreviewLoading(false);
  }, [material]);

  const performSave = useCallback(
    async (overwrite: boolean) => {
      setIsSubmitting(true);
      try {
        const transformParams = buildPreviewPayload();
        const requestPayload: { job_id?: string; transform_params?: TransformPreviewRequest; overwrite: boolean } = previewJobId
          ? { job_id: previewJobId, overwrite }
          : { transform_params: transformParams, overwrite };

        const response = isTemporaryMaterial
          ? await requestTransformSaveTemporary(material.id, requestPayload)
          : await requestTransformSave(material.id, requestPayload);

        if (overwrite) {
          toast.show(isTemporaryMaterial ? '個人フォルダの素材を上書きしました' : '共有フォルダの素材を上書きしました');
        } else {
          toast.show('別名保存しました');
        }

        // 保存後は素材データを更新してプレビューモードへ戻る
        if (response.material) {
          setMaterial(response.material as MaterialData);
        }
        setViewMode('preview');

        if (response.preview_url) {
          setPreviewImageUrl(response.preview_url ?? null);
        }

        setPreviewJobId(null);
        lastPreviewPayloadRef.current = null;
        setIsTransformApplied(true);
        onRequestRefresh?.(material.id);
      } catch (error) {
        console.error('save transform failed', error);
        let message = overwrite ? '保存に失敗しました' : '別名保存に失敗しました';
        if (isAxiosError(error)) {
          const backendMessage = error.response?.data?.error;
          if (typeof backendMessage === 'string' && backendMessage.trim()) {
            message = backendMessage.trim();
          }
        }
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [buildPreviewPayload, isTemporaryMaterial, material.id, onRequestRefresh, previewJobId, toast],
  );

  const handleSave = useCallback(() => {
    void performSave(true);
  }, [performSave]);

  const handleSaveAs = useCallback(() => {
    void performSave(false);
  }, [performSave]);

  const handleDownload = useCallback(async () => {
    if (isDownloading || isPreviewLoading) {
      return;
    }
    setIsDownloading(true);
    try {
      let downloadUrl = previewImageUrl;
      if (!downloadUrl) {
        downloadUrl = (await handleApplyTransform({ showToast: false, force: true })) ?? null;
        if (!downloadUrl) {
          toast.error('ダウンロードに必要なプレビュー生成に失敗しました');
          return;
        }
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('ファイルの取得に失敗しました');
      }
      const blob = await response.blob();
      const contentType = material.content_type || blob.type || 'image/png';

      // 編集モードでは editState.format を優先
      const extension = editState.format || 'png';

      const { generateDownloadFilename } = await import('./downloadUtils');
      const filename = generateDownloadFilename(material, contentType, extension);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.show('ファイルをダウンロードしました');
    } catch (error) {
      console.error('ダウンロードエラー:', error);
      toast.show('ダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
    }
  }, [editState.format, handleApplyTransform, isDownloading, isPreviewLoading, material, previewImageUrl, toast]);

  // initialMaterial が変更されたときに内部状態を更新
  useEffect(() => {
    setMaterial(initialMaterial);
    const newNotes = (initialMaterial as CloudMaterial).notes ?? '';
    setNotes(newNotes);
    setOriginalNotes(newNotes);
    const newName = (initialMaterial as CloudMaterial).name ?? '';
    setMaterialName(newName);
    setOriginalMaterialName(newName);
  }, [initialMaterial]);

  // material.idが変更されたときに編集状態をリセット
  useEffect(() => {
    setViewMode('preview');
    setIsImageLoaded(false);
    const newNotes = (material as CloudMaterial).notes ?? '';
    setNotes(newNotes);
    setOriginalNotes(newNotes);
    const newName = (material as CloudMaterial).name ?? '';
    setMaterialName(newName);
    setOriginalMaterialName(newName);
    setTags([...((material as CloudMaterial).tags ?? [])]);
    invalidatePreview();
    setCropState({ x: 0, y: 0, width: 1, height: 1 });
    setZoom(1);
    setNaturalSize(null);
    setIsPreviewLoading(false);
    setSelectedAspectRatio('custom');

    const formatFromMime = material.content_type?.split('/')[1] ?? 'png';
    const normalizedFormat = formatFromMime === 'jpeg' ? 'jpg' : formatFromMime;

    setEditState({
      width: material.width ?? null,
      height: material.height ?? null,
      selectedResolutionId: null,
      padding: 'none',
      format: normalizedFormat,
      quality: 'medium',
    });
    lastPreviewPayloadRef.current = null;
  }, [invalidatePreview, material.id]);


  const cropEquals = useCallback((a: CropState, b: CropState) => {
    return (
      Math.abs(a.x - b.x) < 0.0001 &&
      Math.abs(a.y - b.y) < 0.0001 &&
      Math.abs(a.width - b.width) < 0.0001 &&
      Math.abs(a.height - b.height) < 0.0001
    );
  }, []);

  const renderPreviewContent = () => {
    if (viewMode === 'edit') {
      return (
        <MaterialEditView
          displayPreviewUrl={displayPreviewUrl}
          selectedAspectRatio={selectedAspectRatio}
          editState={editState}
          cropState={cropState}
          zoom={zoom}
          zoomDisplay={zoomDisplay}
          platformSelectOptions={platformSelectOptions}
          menuSelectOptions={menuSelectOptions}
          specSelectOptions={specSelectOptions}
          selectedPlatformId={selectedPlatformId}
          selectedMenuId={selectedMenuId}
          selectedSpecId={selectedSpecId}
          platformLoading={platformLoading}
          menuLoading={menuLoading}
          specLoading={specLoading}
          menuGuidelinesState={menuGuidelinesState}
          specGuidelinesState={specGuidelinesState}
          mediaResolutionPresets={mediaResolutionPresets}
          allowedAspectRatiosForSidebar={allowedAspectRatiosForSidebar}
          autoFitRef={autoFitRef}
          lastPreviewPayloadRef={lastPreviewPayloadRef}
          viewportRef={viewportRef}
          onZoomStep={handleZoomStep}
          onCanvasInteractionStart={handleCanvasInteractionStart}
          onCanvasInteractionEnd={handleCanvasInteractionEnd}
          onImageLoad={handleImageLoad}
          onCropAspectRatioChange={handleCropAspectRatioChange}
          onSelectPlatform={handleSelectPlatform}
          onSelectMenu={handleSelectMenu}
          onSelectSpec={handleSelectSpec}
          setCropState={setCropState}
          setEditState={setEditState}
          setPreviewJobId={setPreviewJobId}
          setIsTransformApplied={setIsTransformApplied}
          cropEquals={cropEquals}
          onReset={handleReset}
          onApplyTransform={() => {
            void handleApplyTransform({ showToast: true, force: true });
          }}
          isSubmitting={isSubmitting}
          isPreviewLoading={isPreviewLoading}
          hasChanges={hasCropChanges && !isTransformApplied}
          mobileMaxWidth={mobileMaxWidth}
          mobileMaxHeight={mobileMaxHeight}
        />
      );
    }

    return (
      <MaterialPreviewView
        material={material}
        materialId={materialId}
        isVideo={isVideo}
        isAudio={isAudio}
        fullSizeImageUrl={displayImageUrl}
        videoUrl={videoUrl}
        audioUrl={audioUrl}
        mediaLoadError={mediaLoadError}
        showEditControls={showEditControls}
        tags={tags}
        notes={notes}
        isNotesDirty={isNotesDirty}
        materialName={materialName}
        isMaterialNameDirty={isMaterialNameDirty}
        updateMutation={updateMutation}
        onNavigate={onNavigate}
        onTagsChange={handleTagsChange}
        onNotesChange={setNotes}
        onNotesCancel={handleNotesCancel}
        onNotesSave={handleNotesSave}
        onMaterialNameChange={setMaterialName}
        onMaterialNameCancel={handleMaterialNameCancel}
        onMaterialNameSave={handleMaterialNameSave}
        onWorkflowStageChange={handleWorkflowStageChange}
        openProductSelector={openProductSelector}
        navigate={navigate}
        actorModels={actorModels}
        productLinks={productLinks}
        isProductLinksLoading={isProductLinksLoading}
        isScenariosLoading={isScenariosLoading}
        isMobile={isMobile}
        isImageLoading={!isImageLoaded && !!displayImageUrl && !isVideo && !isAudio}
        onImageLoadComplete={() => setIsImageLoaded(true)}
        hasGenerationInfo={hasGenerationInfo((material as Record<string, unknown>).generation_metadata as Record<string, unknown> | undefined)}
        onShowGenerationInfo={() => setShowGenerationInfo(true)}
        facePrivacyTwinUrl={facePrivacyTwinUrl}
        facePrivacyTwinStyle={facePrivacyTwinStyle}
      />
    );
  };

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={(e) => {
        // 背景部分をクリックした場合のみモーダルを閉じる
        if (e.target === e.currentTarget) {
          if (viewMode === 'edit') {
            handleExitEditMode('close');
          } else {
            onClose();
          }
        }
      }}
    >
      <div
        className={`${styles.modal} ${viewMode === 'edit' ? styles.editMode : ''} ${sheetDragging ? styles.modalDragging : ''}`}
        style={isMobile && sheetDragOffset > 0 ? { transform: `translateY(${sheetDragOffset}px)` } : undefined}
      >
        {isMobile && (
          <div
            className={styles.dragHandle}
            onTouchStart={handleSheetSwipeStart}
            onTouchMove={handleSheetSwipeMove}
            onTouchEnd={handleSheetSwipeEnd}
          />
        )}
        {canEdit && viewMode === 'edit' ? (
          <div className={styles.headerActions}>
            <button
              type="button"
              className={backButtonStyles.backButton}
              onClick={() => handleExitEditMode('preview')}
              disabled={isSubmitting}
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className={backButtonStyles.icon}
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className={backButtonStyles.label}>プレビューへ戻る</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        )}
        {renderPreviewContent()}
        {viewMode === 'edit' ? (
          <ImageEditActionBar
            isSubmitting={isSubmitting}
            isPreviewLoading={isPreviewLoading}
            hasChanges={hasEditChanges}
            hasCropChanges={hasCropChanges}
            onReset={handleReset}
            onApplyTransform={() => {
              void handleApplyTransform({ showToast: true, force: true });
            }}
            onSave={handleSave}
            onSaveAs={handleSaveAs}
            onDownload={handleDownload}
            hasTransformApplied={isTransformApplied}
            canSave={!isPreviewLoading && !isSubmitting}
            canDownload={!isPreviewLoading}
          />
        ) : null}
        {viewMode === 'preview' && (
          <MaterialPreviewFooter
            material={material}
            isVideo={isVideo}
            isTemporaryMaterial={isTemporaryMaterial}
            canEdit={canEdit}
            fullSizeImageUrl={fullSizeImageUrl}
            videoUrl={videoUrl}
            thumbnailUrl={materialUrls?.thumbnail ?? null}
            showAddToSelectionButton={showAddToSelectionButton}
            showShareButton={showShareButton}
            generationState={generationState}
            editInCanva={editInCanva}
            isCanvaLoading={isCanvaLoading}
            onYouTubeUpload={generationState ? () => setYoutubeUploadOpen(true) : undefined}
            onScrollVideo={canStartScrollVideo ? () => setScrollVideoOpen(true) : undefined}
            onSetViewMode={setViewMode}
            onSetAdsUploadOpen={setIsAdsUploadOpen}
            onClose={onClose}
            onDeleted={onDeleted}
            onRequestRefresh={onRequestRefresh}
            getPreviewUrl={() => materialUrls?.fullsize ?? null}
            updateCloudMutation={updateCloudMutation}
            updateTemporaryMutation={updateTemporaryMutation}
            saveTemporaryMutation={saveTemporaryMutation}
            deleteTemporaryMutation={deleteTemporaryMutation}
            deleteCloudMutation={deleteCloudMutation}
            moveToPersonalMutation={moveToPersonalMutation}
            setMaterial={setMaterial}
            setIsMoveToPersonalConfirmOpen={setIsMoveToPersonalConfirmOpen}
          />
        )}
      </div>

      {/* YouTubeアップロードモーダル (動画素材) */}
      <YouTubeUploadModal
        source={youtubeUploadOpen
          ? { id: material.id, defaultTitle: materialName || (material as { filename?: string }).filename || '', defaultDescription: '' }
          : null}
        onClose={() => setYoutubeUploadOpen(false)}
        onSubmit={handleStartMaterialYouTubeUpload}
        reopenAfterAuth={false}
      />

      {/* スクロール動画モーダル (画像素材・§11.4)。完了した動画は個人フォルダに保存され、選択中の素材に追加される */}
      {scrollVideoOpen && (
        <Suspense fallback={null}>
          <SwipeClipModal material={{ id: material.id }} onClose={() => setScrollVideoOpen(false)} onSubmit={handleStartScrollVideo} />
        </Suspense>
      )}

      {/* 商品選択モーダル */}
      <ProductSelectorModal
        isOpen={isProductSelectorOpen}
        onClose={() => setIsProductSelectorOpen(false)}
        onSelectLinks={handleProductSelectLinks}
        initialSelectedLinks={selectedProductLinks}
      />

      {/* Amazon広告アップロードモーダル */}
      <AmazonAdsUploadModal
        isOpen={isAdsUploadOpen}
        onClose={() => setIsAdsUploadOpen(false)}
        materialId={material.id}
        materialUrl={fullSizeImageUrl || materialUrls?.fullsize || ''}
        material={material}
        onUploadSuccess={() => {
          toast.show('Amazon広告にアップロードしました');
        }}
      />

      {/* 個人フォルダへ移動の確認モーダル */}
      <ConfirmDialog
        isOpen={isMoveToPersonalConfirmOpen}
        title="個人フォルダへ移動"
        message="この素材は共有フォルダからは削除されて他のユーザーからは見えなくなります。よろしいですか？"
        confirmLabel="移動する"
        variant="warning"
        isLoading={moveToPersonalMutation.isPending}
        onConfirm={async () => {
          try {
            await moveToPersonalMutation.mutateAsync(material.id);
            setIsMoveToPersonalConfirmOpen(false);
            onClose();
          } catch (error) {
            console.error('個人フォルダへの移動エラー:', error);
          }
        }}
        onCancel={() => setIsMoveToPersonalConfirmOpen(false)}
      />


      {/* Canva動画アップロードモーダル */}
      <CanvaVideoUploadModal
        isOpen={videoUploadModal.isOpen}
        isUploading={videoUploadModal.isUploading}
        canvaUrl={videoUploadModal.canvaUrl}
        thumbnailUrl={videoUploadModal.thumbnailUrl}
        progress={videoUploadModal.progress}
        onClose={closeVideoUploadModal}
      />

      {/* 生成情報モーダル */}
      {showGenerationInfo && (
        <GenerationInfoModal
          material={material}
          fullSizeImageUrl={fullSizeImageUrl}
          onClose={() => setShowGenerationInfo(false)}
          onNavigateToGeneration={onClose}
        />
      )}
      <ConfirmDialog
        isOpen={showExitEditConfirm}
        title="編集内容を破棄しますか？"
        message="保存されていない変更は失われます。"
        confirmLabel="破棄する"
        cancelLabel="編集を続ける"
        variant="warning"
        onConfirm={handleConfirmExitEdit}
        onCancel={() => { setShowExitEditConfirm(false); setPendingExitAction(null); }}
      />
    </div>,
    document.body
  );
}
